import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import axios from "axios";

const MAX_FAKE_PROGRESS = 90;

// NOTE: Remember, everytime progress is updated, MUI animates the bar to the new position over the course of 200ms.
// NOTE: From initial evaluation, this kinda looks like it should work if all the below assumptions/questions are met.

const ProgressBar = forwardRef(function ProgressBar(
  {
    duration = 1200,     // time to reach ~90%
    minDuration = 400,  // 👈 minimum visible time
    onComplete,
  },
  ref
) {
  const [progress, setProgress] = useState(100);
  const progressRef = useRef(100);
  const [disableAnimation, setDisableAnimation] = useState(true);
  const rafRef = useRef(null);
  const doneRef = useRef(true);
  const runStartRef = useRef(0);
  const readyRef = useRef(false);
  const resultRef = useRef(null);

  useImperativeHandle(ref, () => ({
    startRun: ({ type, url, payload, fakeData }) => {
      // This resets the progress bar to zero (without animation).
      cancelAnimationFrame(rafRef.current);
      doneRef.current = false;

      // 🔥 Disable transition + hard reset
      // setDisableAnimation(true);
      setProgress(0);
      progressRef.current = 0;
      // console.log("progress ", 0);

      // Re-enable animation next frame
      requestAnimationFrame(() => {
        // setDisableAnimation(false);
      });

      runStartRef.current = Date.now();
      const start = Date.now();

      // Recursive loop until we reach 90%.
      const fakeTick = () => {
        // console.log("fakeTick");
        if (doneRef.current) return;

        const elapsed = Date.now() - start;
        // Value will be counting up to 90, as a percentage of time elapsed
        // When duration has elapsed, 90% is reached, when not, this will animate
        // until (minDuration / duration) * 90% or 33.75% is reached.
        const value = Math.min(
          (elapsed / duration) * MAX_FAKE_PROGRESS,
          MAX_FAKE_PROGRESS
        );

        // Progress is set and updated until it reaches 90%.
        setProgress((p) => {
          let newProgress = Math.max(p, value);
          progressRef.current = newProgress;
          return newProgress;
        });

        // Once we reach 90%, we can stop scheduling fakeTick.
        if (value >= MAX_FAKE_PROGRESS) {
          return; // stop ticking
        }

        // console.log("progress ", progressRef.current);
        rafRef.current = requestAnimationFrame(fakeTick);
      };

      rafRef.current = requestAnimationFrame(fakeTick);

      // NOTE: Does the above block until 90% is finished?
      // Don't think so because of how doneRef is used here and set in finishAnimation...
      // Seems reasonable logic if indeed it does not block.

      // Immediately move on to markReady.
      if (type === "fake") {
        // console.log("fake response");
        markReady({ data: fakeData ?? payload });
        return;
      }

      // Move on to markReady when axios is finished.
      axios
        .post(url, payload, {
          // NOTE: Maybe bring back when all is working properly. Not important now.
          // onUploadProgress: (e) => {
          //   if (!e.total || doneRef.current) return;
          //   const real = Math.round((e.loaded * 100) / e.total);
          //   setProgress((p) => Math.max(p, real));
          // },
        })
        .then((res) => {
          // console.log("real response");
          return markReady(res)
        })
        .catch((err) => markReady(err));
    },
  }));

  // This part basically calculates how much time should be spent before we call it done.
  // Basically, waits until axios is done or minDuration has elapsed; whichever is last.
  const markReady = (result) => {
    // console.log("--> markReady");
    // console.log("data", result.data);
    resultRef.current = result;
    readyRef.current = true;

    const elapsed = Date.now() - runStartRef.current;
    const remaining = Math.max(minDuration - elapsed, 0);

    setTimeout(() => {
      if (readyRef.current) { // Does this actually do anything?
        startFinishAnimation();
      }
    }, remaining);
  };

  /* ---------- Final animation to 100 ---------- */
  const startFinishAnimation = () => {
    // console.log("--> startFinishAnimation");
    if (doneRef.current) return;
    doneRef.current = true;
    cancelAnimationFrame(rafRef.current);

    const startValue = progressRef.current; // This is whatever's left-over. Not correct right now. Needs fix...
    // console.log("startValue ", startValue);
    const finishStart = Date.now();
    // The final duration for the final percentage of progress. Calculated with a minimum to make sure
    // it will be visible to the eye.
    // This doesn't work well for long durations with short minDurations, find a fix...
    // const finishDuration = Math.max(((100 - startValue)/100) * duration, 300);
    const finishDuration = 300;
    // console.log("finishDuration ", finishDuration);

    const tick = () => {
      // console.log("finishTick");
      const elapsed = Date.now() - finishStart;
      const value = Math.min(
        startValue + (elapsed / finishDuration) * (100 - startValue),
        100
      );

      setProgress(value);
      progressRef.current = value;
      // console.log("progress", value);

      if (value < 100) {
        requestAnimationFrame(tick);
      } else {
        onComplete?.(resultRef.current); // 🔥 EXACTLY when bar finishes
      }
    };

    requestAnimationFrame(tick);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          "& .MuiLinearProgress-bar": {
            transition: disableAnimation
              ? "none"
              : "transform 0.2s linear",
          },
        }}
      />
    </Box>
  );
});

export default ProgressBar;
